import { apiConfig } from "./config";
import { adminAuthStorage, visitorAuthStorage } from "./authStorage";
import { debugLog } from "../services/debugLog";
import i18n, { normalizeLocale } from "../i18n";

export class ApiError extends Error {
  readonly code?: string;

  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    if (body && typeof body === "object" && body !== null && "error" in body) {
      this.code = String((body as { error?: unknown }).error ?? "");
    }
  }
}

function apiErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;
  const o = body as { error?: string; message?: string };
  if (o.error) {
    const key = `errors.${o.error}`;
    const translated = i18n.t(key);
    if (translated && translated !== key) {
      // Prefer specific API message only for badge-verify handoff copy.
      if (o.error === "need_badge_verify" && o.message) return o.message;
      return translated;
    }
  }
  if (o.message) return o.message;
  // Never surface raw snake_case error codes to the user.
  return fallback;
}

export function formatApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return apiErrorMessage(err.body, fallback);
  }
  if (err instanceof Error && err.message) {
    const key = `errors.${err.message}`;
    const translated = i18n.t(key);
    if (translated && translated !== key) return translated;
    // Hide technical browser/DOM English messages behind fallback when possible.
    if (/^[a-z0-9_]+$/i.test(err.message) || /NotAllowed|NotReadable|Permission|DOMException/i.test(err.message)) {
      return fallback;
    }
    return err.message;
  }
  return fallback;
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  audience?: "visitor" | "admin";
  /** без токена (login, send-otp) */
  public?: boolean;
  idempotencyKey?: string;
};

function withLangQuery(path: string): string {
  if (optionsAudienceIsAdminPath(path)) return path;
  const lang = normalizeLocale(i18n.language);
  const sep = path.includes("?") ? "&" : "?";
  if (/[?&]lang=/.test(path)) return path;
  return `${path}${sep}lang=${lang}`;
}

function optionsAudienceIsAdminPath(path: string): boolean {
  return path.startsWith("/admin") || path.startsWith("/auth/admin");
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": normalizeLocale(i18n.language),
  };

  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) {
    headers["Content-Type"] = "application/json";
  }

  if (options.idempotencyKey) {
    headers["Idempotency-Key"] = options.idempotencyKey;
  }

  if (!options.public) {
    const storage = options.audience === "admin" ? adminAuthStorage : visitorAuthStorage;
    const token = storage.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const resolvedPath =
    options.audience === "admin" ? path : withLangQuery(path);

  const response = await fetch(`${apiConfig.baseUrl}${resolvedPath}`, {
    method: options.method ?? "GET",
    headers,
    body:
      options.body === undefined
        ? undefined
        : isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body),
  });

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    debugLog.warn("api", `${options.method ?? "GET"} ${path} → ${response.status}`, body);
    throw new ApiError(`API ${response.status}: ${path}`, response.status, body);
  }

  debugLog.info("api", `${options.method ?? "GET"} ${path} → ${response.status}`);

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
